import { useEffect, useMemo, useState } from 'react'
import { ruleText, translate, type Lang } from './i18n'
import type { Evaluation, FormUnit, ShiftRoster } from './api'

/**
 * El armador de tripulación (§9.3).
 *
 * Dos cosas gobiernan el diseño:
 *
 * 1. **Las reglas se revisan mientras se arma, no al guardar.** Quien conforma tiene que ver por
 *    qué no puede antes de invertir cinco clics, no después.
 * 2. **Lo tomado se muestra, no se esconde.** Un oficial que falta de la lista sin explicación es
 *    una llamada a soporte; con el indicativo de su unidad al lado, es una respuesta.
 */
export function CrewBuilder({
  lang, roster, evaluate, onForm, busy, canExcept,
}: {
  lang: Lang
  roster: ShiftRoster | null
  evaluate: (r: FormUnit) => Promise<Evaluation | null>
  onForm: (r: FormUnit) => void
  busy: boolean
  canExcept: boolean
}) {
  const [type, setType] = useState('')
  const [callSign, setCallSign] = useState('')
  const [division, setDivision] = useState('')
  const [crew, setCrew] = useState<string[]>([])
  const [vehicle, setVehicle] = useState('')
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)

  const unitType = useMemo(
    () => roster?.unitTypes.find((t) => t.code === type) ?? roster?.unitTypes[0] ?? null,
    [roster, type])

  const divisions = useMemo(
    () => [...new Set((roster?.officers ?? []).map((o) => o.homeDivision))].sort(),
    [roster])

  const propuesta: FormUnit = useMemo(() => ({
    unitTypeCode: unitType?.code ?? '',
    officerIds: crew,
    vehicleId: vehicle,
    division: division || divisions[0] || '',
    callSign,
  }), [unitType, crew, vehicle, division, divisions, callSign])

  // Las reglas se revisan con la unidad completa: el motor necesita el vehículo para poder
  // opinar, y pedirle que evalúe sin él solo produce un error que no le dice nada a nadie.
  const completa = !!unitType && crew.length > 0 && !!vehicle
  useEffect(() => {
    if (!completa) { setEvaluation(null); return }
    let vigente = true
    void (async () => {
      const resultado = await evaluate(propuesta)
      if (vigente) setEvaluation(resultado)
    })()
    return () => { vigente = false }
  }, [propuesta, completa, evaluate])

  if (!roster) return null

  const duras = (evaluation?.violations ?? []).filter((v) => v.severity === 'Hard')
  const faltanMotivos = duras.some((v) => !(reasons[v.ruleCode] ?? '').trim())
  const puedeConformar =
    !busy && completa && !!callSign.trim() && evaluation !== null &&
    !evaluation.isBlocked &&
    (duras.length === 0 || (canExcept && !faltanMotivos))

  const libres = roster.vehicles.filter((v) => v.assignedTo === null || v.vehicleId === vehicle)

  return (
    <section className="panel">
      <h2>{translate(lang, 'crew.title')}</h2>
      <p className="notice">{translate(lang, 'crew.help')}</p>

      <div className="card">
        <div className="actions">
          <select value={unitType?.code ?? ''} onChange={(e) => { setType(e.target.value); setCrew([]) }}
            aria-label={translate(lang, 'crew.type')}>
            {roster.unitTypes.map((t) => (
              <option key={t.code} value={t.code}>{t.name} ({t.minCrew}–{t.maxCrew})</option>
            ))}
          </select>
          <select value={division || divisions[0] || ''} onChange={(e) => setDivision(e.target.value)}
            aria-label={translate(lang, 'crew.division')}>
            {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input
            value={callSign}
            onChange={(e) => setCallSign(e.target.value.toUpperCase())}
            placeholder={translate(lang, 'crew.callSign')}
            style={{ width: 110 }}
            aria-label={translate(lang, 'crew.callSign')}
          />
        </div>

        <div className="meta">
          {translate(lang, 'crew.officers', [crew.length, unitType?.maxCrew ?? 0])}
        </div>
        <div className="picker">
          {roster.officers.map((o) => {
            const tomado = o.assignedTo !== null
            const elegido = crew.includes(o.officerId)
            return (
              <label key={o.officerId} className={`pick ${tomado ? 'taken' : ''} ${elegido ? 'chosen' : ''}`}>
                <input
                  type="checkbox"
                  checked={elegido}
                  disabled={tomado || (!elegido && crew.length >= (unitType?.maxCrew ?? 1))}
                  onChange={(e) => setCrew(e.target.checked
                    ? [...crew, o.officerId]
                    : crew.filter((x) => x !== o.officerId))}
                />
                <span className="grow">{o.badge} · {o.rank} · {o.homeDivision}</span>
                {/* Que no se puede contar con él se ve antes de elegirlo; por qué, no: el motivo es
                    de RH y del mando (PD-06, H-85). */}
                {o.availability !== 'Available' && (
                  <span className={`chip ${o.availability === 'Absent' ? 'danger' : 'warning'}`}>{translate(lang, `availability.${o.availability}`)}</span>
                )}
                <span className="meta">
                  {tomado
                    ? translate(lang, 'crew.taken', [o.assignedTo!])
                    : translate(lang, 'crew.years', [o.years])}
                </span>
              </label>
            )
          })}
        </div>

        <div className="actions">
          <select value={vehicle} onChange={(e) => setVehicle(e.target.value)}
            aria-label={translate(lang, 'crew.vehicle')}>
            <option value="">{translate(lang, 'crew.pickVehicle')}</option>
            {/* El estado va en la etiqueta: un vehículo en taller se ve antes de elegirlo, no
                después de que la regla lo rechace. */}
            {libres.map((v) => (
              <option key={v.vehicleId} value={v.vehicleId}>
                {v.unitNumber} · {v.vehicleType} · {v.baseDivision}
                {v.status === 'Available' ? '' : ` · ${translate(lang, `vehicle.${v.status}`)}`}
                {v.availability === 'Available' ? '' : ` · ${translate(lang, `availability.vehicle.${v.availability}`)}`}
              </option>
            ))}
          </select>
        </div>

        {/* Siempre hay algo que decir: qué falta para poder revisar, que está limpio, o que la
            revisión no se pudo hacer. Callarse deja al planner adivinando. */}
        {!completa && crew.length > 0 && (
          <div className="meta">{translate(lang, 'crew.pickVehicleFirst')}</div>
        )}
        {completa && evaluation === null && (
          <div className="meta error">{translate(lang, 'crew.cannotCheck')}</div>
        )}
        {evaluation && evaluation.violations.length === 0 && (
          <div className="meta ok-text">{translate(lang, 'crew.clean')}</div>
        )}

        {(evaluation?.violations ?? []).map((v) => (
          <div key={v.ruleCode} className={`violation ${v.severity.toLowerCase()}`}>
            <div className="row-line">
              <strong className="grow">{translate(lang, `rule.${v.ruleCode}`)}</strong>
              <span className="chip">{translate(lang, `severity.${v.severity}`)}</span>
            </div>
            <div className="meta">{ruleText(lang, v)}</div>
            {v.severity === 'Hard' && (canExcept
              ? (
                <input
                  value={reasons[v.ruleCode] ?? ''}
                  onChange={(e) => setReasons({ ...reasons, [v.ruleCode]: e.target.value })}
                  placeholder={translate(lang, 'crew.exceptionReason')}
                  style={{ width: '100%' }}
                />
              )
              : <div className="meta">{translate(lang, 'crew.cannotExcept')}</div>)}
          </div>
        ))}

        {evaluation?.isBlocked && <div className="meta error">{translate(lang, 'crew.blocked')}</div>}
        {completa && !callSign.trim() && <div className="meta">{translate(lang, 'crew.needCallSign')}</div>}

        <button
          className="primary"
          disabled={!puedeConformar}
          onClick={() => {
            onForm({
              ...propuesta,
              exceptions: duras.map((v) => ({ ruleCode: v.ruleCode, reason: reasons[v.ruleCode] ?? '' })),
            })
            setCrew([]); setVehicle(''); setCallSign(''); setReasons({}); setEvaluation(null)
          }}
        >
          {translate(lang, 'crew.form')}
        </button>
      </div>
    </section>
  )
}
